<template>
    <PlanoEngine
        @search-changed="e => $emit('search-changed', e)"
        :catalogPanes="favoriteCatalogPanes"
        :catalogItems="catalogItems"
        :systemType="systemType"
        @save="$emit('save');"
    ></PlanoEngine>
</template>

<script>
import PlanoEngine from "./plano-engine";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import favoritesService from "@/services/favorites";
import { Context, Tools } from "plano_engine";
import componentService from "@/services/component";
import shadeService from "@/services/shades";
import { applyDetailsToModuleElements } from "./utils";

const favoritesPane = {
    label: {
        en: "Favorites",
        fr: "Favoris"
    },
    predicate: builder => builder instanceof Tools.FavoriteItemBuilder
};

export default {
    props: ["catalogPanes", "catalogItems", "systemType", "brandId", "zoneId", "campaignId"],
    components: { PlanoEngine },
    mixins: [TryLoadMixin],
    mounted() {
        this.originAddToFavorites = Context.addToFavorites;
        this.originRemoveFromFavorites = Context.removeFromFavorites;

        Context.addToFavorites = this.addToFavorites.bind(this);
        Context.removeFromFavorites = this.removeFromFavorites.bind(this);
        this.loadFavorites();
    },
    watch: {
        hasAllId() {
            this.loadFavorites();
        }
    },
    methods: {
        async loadFavorites() {
            if (!this.hasAllId) return;
            const favoritesJSON = await this.tryLoad(() =>
                favoritesService.getAll({
                    systemType: this.systemType,
                    brandId: this.brandId,
                    zoneId: this.zoneId,
                    campaignId: this.campaignId
                })
            );
            // ---- rasterization ---
            function visitElement(element, visitor) {
                if (!element) return;
                visitor(element);
                const children = element.children && element.children();

                for (const child of children || []) {
                    visitElement(child, visitor);
                }
            }

            /** @type {Map<number, BoardModuleElement[]>} */
            const moduleElements = new Map();
            const shadeIds = new Set();
            const favoriteElements = [];
            for (const favoriteObject of favoritesJSON) {
                const favorite = Context.deserialize(favoriteObject.favorite);
                favoriteElements.push(favorite);
                for (const item of favorite) {
                    visitElement(item, e => {
                        if (e instanceof BoardModuleElement) {
                            const previousList = moduleElements.get(e.modelId());
                            moduleElements.set(e.modelId(), previousList ? [...previousList, e] : [e]);

                            for (const value of Object.values(e.shadeByGroup)) {
                                shadeIds.add(value);
                            }
                        }
                    });
                }
            }

            const components = await componentService.fromIds([...moduleElements.keys()], {
                brandId: this.brandId,
                zoneId: this.zoneId,
                campaignId: this.campaignId
            });

            const shades = await shadeService.fromIds([...shadeIds], {
                brandId: this.brandId,
                zoneId: this.zoneId,
                campaignId: this.campaignId
            });

            // convert List<List<BoardModuleElement>> into List<BoardModuleElement>
            const allElements = [...moduleElements.values()].reduce((acc, value) => {
                acc.push(...value);
                return acc;
            }, []);

            await applyDetailsToModuleElements(allElements, components, shades);

            for (let i = 0; i < favoritesJSON.length; i++) {
                const favorite = favoriteElements[i];
                const item = favoritesJSON[i];
                const builder = Context.restoreFavorites(favorite);
                builder.setId(item.id);
                if (item.name) {
                    builder.setLabel(
                        item.name,
                        // the default label size
                        45
                    );
                }
            }
        },
        async addToFavorites(element) {
            const builder = this.originAddToFavorites(element);
            const { id, name } = await this.tryLoad(() =>
                favoritesService.saveAt({
                    systemTypeId: this.systemType,
                    brandId: this.brandId,
                    zoneId: this.zoneId,
                    campaignId: this.campaignId,
                    content: Context.serialize(builder.proto)
                })
            );
            builder.setId(id);
            builder.setLabel(name, 45);
            return builder;
        },
        async removeFromFavorites(builder) {
            if (!(await this.dialogConfirm(this.trads.confirmDeleteFavorite))) return;
            this.originRemoveFromFavorites(builder);
            const id = builder.getId();
            if (id) return await this.tryLoad(() => favoritesService.removeAt(id));
        }
    },
    computed: {
        favoriteCatalogPanes() {
            return { ...this.catalogPanes, favoritesPane };
        },
        hasAllId() {
            return !!(this.systemType && this.brandId && this.zoneId && this.campaignId);
        },
        trads() {
            return this.$trad.plano.editor;
        }
    },
    beforeDestroy() {
        stop();
        Context.removeFromFavorites = this.originRemoveFromFavorites;
        Context.addToFavorites = this.originAddToFavorites;
    }
};
</script>

<style></style>



// WEBPACK FOOTER //
// favorite-plano-engine.vue?a6854274